'use client';

import { Suspense, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import { CalendarCheck, Home, PlaneTakeoff, CheckCircle, MapPin, Users } from "lucide-react";
import { track } from "@lib/track";
import MarketingOptIn from "@/app/components/MarketingOptIn";
import { getUpcomingRetreats, type Retreat } from "@/app/data/retreats";
import { ROOM_TYPES, getRoomPricingOptional, type RoomTypeKey } from "@/app/data/retreatPricing";
import {
  RETREAT_HIGHLIGHTS,
  RETREAT_INCLUDED,
  RETREAT_PAYABLE_EXTRAS,
  type InclusionIcon,
} from "@/app/data/retreatInclusions";

// Upcoming only — a past retreat must never be selectable on the deposit form.
const cfRetreats = getUpcomingRetreats().filter((r) => r.type === "cf");

// Room options resolve through the same env-backed helper `/api/retreats/initiate`
// charges from. The old form carried its own hardcoded 2300/2800 pair, so the page
// could quote one price while SumUp billed another. `getRoomPricingOptional` returns
// null on an unset fee var rather than throwing, so a misconfigured deploy drops the
// price from the copy instead of blanking the booking page.
const ROOM_OPTIONS = ROOM_TYPES.map((key) => ({ key, pricing: getRoomPricingOptional(key) }));

// The price belongs above the fold, not 400px down inside step 2.
//
// Of 27 sessions that reached this page in a month, one attempted a booking —
// and most of them arrived from the hero CTA on /retreats, where the first
// price doesn't appear until word 1,166 of 2,025. So the usual journey was
// "click Reserve → meet £2,300 for the first time → leave". Whatever else is
// true, nobody should reach a deposit form without having been told the number.
//
// Derived, never hardcoded: same `getRoomPricingOptional` the form and
// `/api/retreats/initiate` use, so a fee change can't leave the hero quoting a
// figure checkout won't honour. Null (unset env var) drops the line rather than
// blanking the page.
const SINGLE_PRICING = getRoomPricingOptional("Single (own)");
const COUPLE_PRICING = getRoomPricingOptional("Shared (Couple)");

const ICONS: Record<InclusionIcon, typeof CalendarCheck> = {
  CalendarCheck,
  Home,
  PlaneTakeoff,
};

const gbp = (n: number) => `£${n.toLocaleString("en-GB")}`;

function paymentDueFor(retreat: Retreat) {
  const d = new Date(retreat.startDate);
  d.setMonth(d.getMonth() - 1);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

const labelCls = "block text-sm font-semibold text-[var(--color-text-brand)] mb-1.5";
// Theme tokens, not `bg-white`: globals.css ships a real `prefers-color-scheme: dark`
// palette, and a hardcoded white field would pair with light ink text there.
const inputCls =
  "block w-full rounded-lg p-3 border border-[var(--border-on-light)] bg-[var(--bg-surface-dk)] text-[var(--color-text-ink)] focus:border-[var(--color-brand)] focus:ring-1 focus:ring-[var(--color-brand)] outline-none";

function BookingForm() {
  const searchParams = useSearchParams();

  // Every "Reserve Your Spot" CTA now carries ?retreat=<id>. Landing on the form
  // with the week you just clicked already chosen is the point: the old form made
  // you re-find it in a dropdown of two near-identical June 2027 dates, at the
  // bottom of the page, after you'd already been quoted a price.
  const preselectedId = searchParams.get("retreat");
  const initialIdx = useMemo(() => {
    const fromQuery = cfRetreats.findIndex((r) => r.id === preselectedId);
    if (fromQuery !== -1) return fromQuery;
    return cfRetreats.length === 1 ? 0 : "";
  }, [preselectedId]);

  const [selectedRetreatIdx, setSelectedRetreatIdx] = useState<number | "">(initialIdx);
  const [roomType, setRoomType] = useState<RoomTypeKey | "">("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [secondName, setSecondName] = useState("");
  const [secondEmail, setSecondEmail] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setSelectedRetreatIdx(initialIdx), [initialIdx]);

  const selectedRetreat = selectedRetreatIdx !== "" ? cfRetreats[selectedRetreatIdx] : null;
  const pricing = roomType ? getRoomPricingOptional(roomType) : null;
  const isCouple = pricing?.isCouple ?? false;
  const deposit = pricing?.deposit ?? 0;
  const total = pricing?.price ?? 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedRetreat) {
      setError("Please choose which retreat week you'd like.");
      return;
    }
    if (!roomType) {
      setError("Please choose a room type.");
      return;
    }

    // Label and component are unchanged on purpose — this event series is read by
    // the reporting pipeline and goes back to 2026-06. Only `retreatId` is new.
    track(
      "cta_click",
      { label: "Retreats | Book", component: "RetreatBookingPage" },
      {
        event: "cta_click",
        label: "Retreats | Book",
        component: "RetreatBookingPage",
        meta: { roomType, deposit, retreatId: selectedRetreat.id },
      }
    );

    setSubmitting(true);
    try {
      const res = await fetch(`/api/retreats/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          retreatId: selectedRetreat.id,
          roomType,
          name,
          email,
          secondName: isCouple ? secondName : undefined,
          secondEmail: isCouple ? secondEmail : undefined,
          marketingOptIn,
        }),
      });
      const data = await res.json();
      if (res.ok && data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        console.error("Retreat booking failed:", data);
        setError(data.error || "We couldn't start the payment. Please try again, or email us and we'll reserve your place by hand.");
        setSubmitting(false);
      }
    } catch (err) {
      console.error("Error creating payment link:", err);
      setError("Something went wrong reaching our payment provider. Please try again, or email us and we'll reserve your place by hand.");
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--bg-surface-dk)]">
      {/* ── Hero ──
          The scrim is heaviest at the bottom, where the text sits. The previous
          version pulled the heading up over the image with a negative margin and
          faded *to* the page background, so white type landed on the lightest
          part of the gradient. */}
      <header className="relative h-[32vh] min-h-[260px] w-full overflow-hidden">
        <Image
          src="/images/laMangaCut.webp"
          alt="Villa Gawy, the beachside retreat venue in La Manga, Spain"
          fill
          priority
          className="object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-5xl mx-auto px-6 pb-7">
            <p className="text-white/75 text-xs font-semibold tracking-[0.15em] uppercase mb-2">
              Reserve your place
            </p>
            <h1 className="text-3xl md:text-4xl font-black text-white leading-[1.15] max-w-3xl">
              The Art of Eating, Moving, Breathing &amp; Being
            </h1>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-white/85">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" aria-hidden="true" /> La Manga, Spain
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4" aria-hidden="true" /> Limited to 18 guests
              </span>
              {selectedRetreat && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarCheck className="h-4 w-4" aria-hidden="true" /> {selectedRetreat.displayDates}
                </span>
              )}
            </div>

            {SINGLE_PRICING && (
              <p className="mt-3 text-sm text-white/90">
                <span className="font-black text-white">{gbp(SINGLE_PRICING.price)}</span> per person
                {COUPLE_PRICING && (
                  <> · <span className="font-black text-white">{gbp(COUPLE_PRICING.price)}</span> per room for two</>
                )}
                <span className="block sm:inline sm:before:content-['_·_']">
                  {gbp(SINGLE_PRICING.deposit)} deposit today, balance a month before you travel
                </span>
              </p>
            )}
          </div>
        </div>
      </header>

      {/* ── Three headline inclusions, above the form ──
          Deliberately compact: five of the ten sessions that reached this page left
          inside 26 seconds and four went back to /retreats, so something concrete
          needs to be here — but not so much of it that the form is pushed off a
          phone screen. */}
      <section className="px-6 py-9 bg-[var(--bg-surface)]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {RETREAT_HIGHLIGHTS.map(({ icon, title, body }) => {
            const Icon = ICONS[icon];
            return (
              <div key={title} className="flex gap-3">
                <Icon className="h-5 w-5 text-[var(--color-text-brand)] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h2 className="text-sm font-black text-[var(--color-text-brand)] leading-snug">{title}</h2>
                  <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed mt-0.5">{body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── The form ──
          Single centred column. The old two-column grid put a 19-item checklist
          beside a seven-field form and then ran the add-ons across three of five
          columns, leaving a two-column hole down the right. Nothing here can go
          lopsided because nothing sits beside anything. */}
      <section className="px-6 py-14">
        <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-[var(--bg-surface)] ring-1 ring-black/5 rounded-2xl p-6 md:p-8 space-y-8">
          {/* 1. Which week — first, because it's the decision they already made */}
          <fieldset>
            <legend className={labelCls}>1. Choose your week</legend>
            <div className="space-y-2">
              {cfRetreats.map((r, i) => (
                <label
                  key={r.startDate}
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition ${
                    selectedRetreatIdx === i
                      ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5 ring-1 ring-[var(--color-brand)]"
                      : "border-[var(--border-on-light)] hover:border-[var(--color-brand)]/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="retreatWeek"
                    checked={selectedRetreatIdx === i}
                    onChange={() => setSelectedRetreatIdx(i)}
                    className="h-4 w-4 accent-[var(--color-brand)]"
                    required
                  />
                  <span className="text-sm font-semibold text-[var(--color-text-ink)]">{r.displayDates}</span>
                </label>
              ))}
              {cfRetreats.length === 0 && (
                <p className="text-sm text-[var(--text-on-light-muted)]">
                  No retreat dates are open for booking right now — please get in touch and we&apos;ll let you know as soon as the next one opens.
                </p>
              )}
            </div>
          </fieldset>

          {/* 2. Room type — prices straight from the env-backed fee config */}
          <fieldset>
            <legend className={labelCls}>2. Choose your room</legend>
            <div className="space-y-2">
              {ROOM_OPTIONS.map((opt) => (
                <label
                  key={opt.key}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
                    roomType === opt.key
                      ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5 ring-1 ring-[var(--color-brand)]"
                      : "border-[var(--border-on-light)] hover:border-[var(--color-brand)]/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="roomType"
                    checked={roomType === opt.key}
                    onChange={() => setRoomType(opt.key)}
                    className="mt-1 h-4 w-4 accent-[var(--color-brand)]"
                    required
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-[var(--color-text-ink)]">
                      {opt.pricing?.label ?? opt.key}
                    </span>
                    {opt.pricing && (
                      <span className="block text-sm text-[var(--text-on-light-muted)]">
                        {gbp(opt.pricing.price)} {opt.pricing.isCouple ? "per room, for two" : "per person"} · {gbp(opt.pricing.deposit)} deposit
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* 3. Guest details */}
          <fieldset className="space-y-4">
            <legend className={labelCls}>3. Your details</legend>
            <div>
              <label htmlFor="guestName" className="block text-sm text-[var(--text-on-light-muted)] mb-1">Full name</label>
              <input id="guestName" type="text" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" className={inputCls} />
            </div>
            <div>
              <label htmlFor="guestEmail" className="block text-sm text-[var(--text-on-light-muted)] mb-1">Email</label>
              <input id="guestEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className={inputCls} />
            </div>

            {isCouple && (
              <>
                <div>
                  <label htmlFor="secondName" className="block text-sm text-[var(--text-on-light-muted)] mb-1">Second guest&apos;s full name</label>
                  <input id="secondName" type="text" value={secondName} onChange={(e) => setSecondName(e.target.value)} required className={inputCls} />
                </div>
                <div>
                  <label htmlFor="secondEmail" className="block text-sm text-[var(--text-on-light-muted)] mb-1">Second guest&apos;s email</label>
                  <input id="secondEmail" type="email" value={secondEmail} onChange={(e) => setSecondEmail(e.target.value)} required className={inputCls} />
                </div>
              </>
            )}
          </fieldset>

          {/* 4. What you're about to pay — stated before the button, not after */}
          {pricing && (
            <div className="rounded-xl bg-[var(--bg-surface-dk)] p-5 space-y-2">
              <div className="flex justify-between text-sm text-[var(--text-on-light-muted)]">
                <span>Retreat total</span>
                <span>{gbp(total)}</span>
              </div>
              <div className="flex justify-between text-base font-black text-[var(--color-text-brand)]">
                <span>Deposit today</span>
                <span>{gbp(deposit)}</span>
              </div>
              <div className="flex justify-between text-sm text-[var(--text-on-light-muted)] pt-2 border-t border-[var(--border-on-light)]">
                <span>Balance{selectedRetreat ? `, due ${paymentDueFor(selectedRetreat)}` : ", due one month before"}</span>
                <span>{gbp(total - deposit)}</span>
              </div>
            </div>
          )}

          <MarketingOptIn checked={marketingOptIn} onChange={setMarketingOptIn} />

          {error && (
            <p role="alert" className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm p-3">
              {error}
            </p>
          )}

          <div>
            <button
              type="submit"
              disabled={submitting || cfRetreats.length === 0}
              className="w-full rounded-lg bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] py-3.5 px-4 font-bold text-base hover:bg-[var(--btn-primary-hover)] transition disabled:opacity-60"
            >
              {submitting
                ? "Taking you to secure payment…"
                : pricing
                  ? `Pay ${gbp(deposit)} deposit & reserve`
                  : "Reserve my place"}
            </button>
            <p className="mt-3 text-xs text-[var(--text-on-light-muted)] text-center">
              You&apos;ll be taken to SumUp to pay securely. Your place is held once the deposit clears.
            </p>
          </div>
        </form>
        </div>
      </section>

      {/* ── Everything in the price, full width ── */}
      <section className="px-6 py-14 bg-[var(--bg-surface)]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] mb-2">
            Everything included in your price
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] mb-8">
            All of the below is covered by your retreat price — no surprises on arrival.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {RETREAT_INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-[var(--text-on-light-muted)]">
                <CheckCircle className="h-4 w-4 text-[var(--color-text-brand)] flex-shrink-0 mt-0.5" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Add-ons last, as asked — it's the one list that isn't in the price ── */}
      <section className="px-6 py-14">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] mb-2">
            Available to add on
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] mb-8">
            A full menu of 1:1 specialist treatments, bookable on arrival — these are not included in the price.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {RETREAT_PAYABLE_EXTRAS.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-[var(--text-on-light-muted)]">
                <span className="text-[var(--color-text-brand)] font-bold flex-shrink-0 mt-0.5 w-4 text-center leading-none">+</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

// useSearchParams needs a Suspense boundary or the build fails on this route.
export default function Page() {
  return (
    <Suspense fallback={<div className="min-h-[60vh]" />}>
      <BookingForm />
    </Suspense>
  );
}
